import React, { useEffect, useState, useRef } from 'react';
import {
  SelectionsSection,
  AreasContainer,
  OptionsContainer,
  QuoteController,
  QuoteActionsContainer,
  PrintHeader,
  Disclaimer,
} from '.';
import { DEFAULT_ACTIVE } from '../constants';
import { updateSelections, getDefaultOptionsObj } from '../utils';
import { fetchBodyAreas } from '../firebase/fetchBodyAreas.js';
import { fetchLenders } from '../firebase/fetchLenders.ts';
import { fetchStates } from '../firebase/fetchStates.ts';
import Footer from './Footer';
import useDates from '../hooks/useDates';
import { initialOptionValues } from '../constants';
import { updateOptionProps } from '../utils';

export const QuoteContext = React.createContext();

const SPLIT_FINANCE_FEATURE =
  process.env.REACT_APP_SPLIT_FINANCE_FEATURE === 'false' ||
  !process.env.REACT_APP_SPLIT_FINANCE_FEATURE
    ? false
    : true;

export function QuoteManager() {
  // 3 options to choose from
  const [options, setOptions] = useState(getDefaultOptionsObj());

  // Keep all the active actions in one state object
  const [active, setActive] = useState(DEFAULT_ACTIVE);

  const [bodyAreas, setBodyAreas] = useState([]); // This contains a group of Parent Areas such as Torso, Legs, Arms ...etc
  const [lenders, setLenders] = useState([]);
  const [states, setStates] = useState([]);
  const [loading, setLoading] = useState(true);
  const printRef = useRef(null);

  // Fetch body areas, lenders and states once on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [fetchedBodyAreas, fetchedLenders, fetchedStates] =
          await Promise.all([fetchBodyAreas(), fetchLenders(), fetchStates()]);
        setBodyAreas(fetchedBodyAreas);
        setLenders(fetchedLenders);
        setStates(fetchedStates);
      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Clear ALL Options Button
  const [clearOptionsClicked, setClearOptionsClicked] = useState(false);
  const handleClearOptions = () => {
    setOptions(getDefaultOptionsObj());
    if (localStorage.getItem('stateName') !== null) {
      DEFAULT_ACTIVE.state = states.find(
        (state) => state.name === localStorage.getItem('stateName')
      );
    }
    setActive(DEFAULT_ACTIVE);
    setClearOptionsClicked(true);
    setTimeout(() => {
      setClearOptionsClicked(false);
    }, 500);
  };

  // Clear Option
  const clearOption = (key) => {
    setOptions((prev) => ({ ...prev, [key]: initialOptionValues }));
    if (options[1].items.length === 0) {
      // if Option 1 is empty -> make it active
      setActive((prev) => ({ ...prev, optionKey: 1 }));
    } else {
      // else: make the cleared Option active
      setActive((prev) => ({ ...prev, optionKey: +key }));
    }
    // Finally: If the previous Option is empty -> make it active
    if (key > 1 && options[key - 1].items.length === 0) {
      setActive((prev) => ({ ...prev, optionKey: key - 1 }));
    }
  };

  // Updating Selections in the AreasContainer happen here when
  // 1. switching between Options OR
  // 2. an area is added or removed in the ACTIVE Option
  const activeOptionItems = options[active.optionKey].items;
  useEffect(() => {
    // This func does nothing to the options props
    // It only selects or un select body areas on the top of the app
    updateSelections(bodyAreas, activeOptionItems);
  }, [active.optionKey, activeOptionItems, bodyAreas]);

  // When any prop in the active object changes, All Options must be updated
  useEffect(() => {
    setOptions(() => {
      let optionsClone = { ...options };
      for (let key in optionsClone) {
        optionsClone[key] = updateOptionProps(options[key], active);
      }
      return optionsClone;
    });
  }, [active]);

  // The ACTIVE option props must update when:
  // 1. adding or removing items
  // 2. option down payment changes
  // 3. the finalTotalCost OR the totalFinanced
  const { downpayment, finalTotalCost, totalFinanced } =
    options[active.optionKey];

  useEffect(() => {
    setOptions((prev) => ({
      ...prev,
      [active.optionKey]: updateOptionProps(options[active.optionKey], active),
    }));
  }, [activeOptionItems, downpayment, finalTotalCost, totalFinanced]);

  // Context Values to be passed into nested components
  const contextValue = {
    options,
    setOptions,
    active,
    setActive,
    handleClearOptions,
    clearOption,
  };

  const dates = useDates(options, active);

  return (
    <main className='container-xxl milan-bg-white'>
      <div className='row'>
        <QuoteContext.Provider value={contextValue}>
          {loading ? (
            <div
              className='d-flex justify-content-center align-items-center'
              style={{ minHeight: '400px' }}
            >
              <div className='text-center'>
                <div className='spinner-border' role='status'>
                  <span className='visually-hidden'>Loading...</span>
                </div>
                <p className='mt-3'>Loading...</p>
              </div>
            </div>
          ) : (
            <>
              <QuoteController />
              <AreasContainer
                parentsAreasArr={bodyAreas}
                clearOptionsClicked={clearOptionsClicked}
              />
              <div id='print-container' className='px-0' ref={printRef}>
                <PrintHeader {...{ active }} />
                <SelectionsSection
                  splitFinanceFeature={SPLIT_FINANCE_FEATURE}
                  allStates={states}
                  allLenders={lenders}
                />
                <OptionsContainer
                  options={options}
                  splitFinanceFeature={SPLIT_FINANCE_FEATURE}
                />
                <div className='container-fluid print-display'>
                  <Disclaimer dates={dates} textColor='navy' />
                </div>
              </div>
              <QuoteActionsContainer
                {...{ options, printRef, active, dates }}
              />
            </>
          )}
        </QuoteContext.Provider>
      </div>
      <Footer />
    </main>
  );
}

export default QuoteManager;
